import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Hotel,
  Bus,
  Plane,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { useSessionState } from '@/hooks/use-session-state';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isWithinInterval,
} from 'date-fns';

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-500',
  TOUR_SEAT: 'bg-purple-500',
  TRANSFER_VEHICLE: 'bg-green-500',
  FLIGHT_SEAT: 'bg-sky-500',
};

const typeBgColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-50 border border-blue-200 hover:bg-blue-100',
  TOUR_SEAT: 'bg-purple-50 border border-purple-200 hover:bg-purple-100',
  TRANSFER_VEHICLE: 'bg-green-50 border border-green-200 hover:bg-green-100',
  FLIGHT_SEAT: 'bg-sky-50 border border-sky-200 hover:bg-sky-100',
};

export function AllocationsCalendarPage() {
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [typeFilter, setTypeFilter] = useSessionState<AllocationType | undefined>(
    'travel:allocations-calendar:typeFilter',
    undefined
  );
  const [selectedPartner, setSelectedPartner] = useSessionState<string | undefined>(
    'travel:allocations-calendar:selectedPartner',
    undefined
  );

  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();

  const { data, isLoading } = trpc.allocation.getAvailabilityCalendar.useQuery({
    month,
    year,
    allocationType: typeFilter,
    partnerId: selectedPartner,
  });

  const { data: partners } = trpc.partner.list.useQuery({ page: 1 });

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad days to start on Sunday
  const startPadding = monthStart.getDay();
  const paddedDays = [...Array(startPadding).fill(null), ...days];

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  const getAllocationsForDay = (day: Date) => {
    if (!data) return [];
    return data.filter((allocation) => {
      const start = new Date(allocation.validFrom);
      const end = new Date(allocation.validTo);
      return isWithinInterval(day, { start, end }) && allocation.availableUnits > 0;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/travel/allocations">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('calendar.title')}</h1>
            <p className="text-muted-foreground">{t('calendar.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={typeFilter ?? ''}
          onChange={(e) => setTypeFilter((e.target.value as AllocationType) || undefined)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('filters.allTypes')}</option>
          <option value="HOTEL_ROOM">{t('types.hotelRoom')}</option>
          <option value="TOUR_SEAT">{t('types.tourSeat')}</option>
          <option value="TRANSFER_VEHICLE">{t('types.transferVehicle')}</option>
          <option value="FLIGHT_SEAT">{t('types.flightSeat')}</option>
        </select>
        <select
          value={selectedPartner ?? ''}
          onChange={(e) => setSelectedPartner(e.target.value || undefined)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('filters.allPartners')}</option>
          {partners?.partners.map((partner) => (
            <option key={partner.id} value={partner.id}>
              {partner.companyName}
            </option>
          ))}
        </select>

        {/* Legend */}
        <div className="ml-auto flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 bg-blue-500" />
            <span>{t('types.hotelRoom')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 bg-purple-500" />
            <span>{t('types.tourSeat')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 bg-green-500" />
            <span>{t('types.transferVehicle')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 bg-sky-500" />
            <span>{t('types.flightSeat')}</span>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={goToToday}>
            {t('calendar.today')}
          </Button>
        </div>
        <h2 className="text-xl font-semibold">{format(currentDate, 'MMMM yyyy')}</h2>
        <div className="w-[200px]" /> {/* Spacer for centering */}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
          <span className="text-muted-foreground ml-2">{t('loading')}</span>
        </div>
      )}

      {/* Calendar Grid */}
      {!isLoading && (
        <div className="border-border bg-card overflow-hidden border">
          {/* Day Headers */}
          <div className="bg-muted/50 grid grid-cols-7 border-b">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-muted-foreground p-3 text-center text-sm font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {paddedDays.map((day, index) => {
              if (!day) {
                return (
                  <div
                    key={`pad-${index}`}
                    className="bg-muted/20 min-h-[120px] border-b border-r"
                  />
                );
              }

              const allocations = getAllocationsForDay(day);
              const isToday = isSameDay(day, new Date());

              return (
                <div
                  key={day.toISOString()}
                  className={`min-h-[120px] border-b border-r p-2 ${
                    !isSameMonth(day, currentDate) ? 'bg-muted/20' : ''
                  }`}
                >
                  <div
                    className={`mb-2 text-sm font-medium ${
                      isToday
                        ? 'bg-primary text-primary-foreground flex h-6 w-6 items-center justify-center'
                        : ''
                    }`}
                  >
                    {format(day, 'd')}
                  </div>

                  <div className="space-y-1">
                    {allocations.slice(0, 3).map((allocation) => {
                      const Icon = typeIcons[allocation.allocationType as AllocationType];
                      return (
                        <Link
                          key={allocation.id}
                          to={`/travel/allocations/${allocation.id}`}
                          className={`block truncate px-1.5 py-0.5 text-xs transition-colors ${typeBgColors[allocation.allocationType as AllocationType]}`}
                        >
                          <Icon className="mr-1 inline h-3 w-3" />
                          {allocation.availableUnits} - {allocation.name}
                        </Link>
                      );
                    })}
                    {allocations.length > 3 && (
                      <div className="text-muted-foreground text-center text-xs">
                        +{allocations.length - 3} {tCommon('more')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Allocations Summary */}
      {!isLoading && data && data.length > 0 && (
        <div className="border-border bg-card border p-6">
          <h3 className="mb-4 font-semibold">{t('calendar.activeAllocations')}</h3>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {data.map((allocation) => {
              const Icon = typeIcons[allocation.allocationType as AllocationType];
              return (
                <Link
                  key={allocation.id}
                  to={`/travel/allocations/${allocation.id}`}
                  className="border-border hover:bg-muted/50 flex items-center gap-3 border p-3 transition-colors"
                >
                  <div
                    className={`flex h-8 w-8 items-center justify-center ${typeColors[allocation.allocationType as AllocationType]} text-white`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{allocation.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {allocation.partner.companyName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-600">{allocation.availableUnits}</p>
                    <p className="text-muted-foreground text-xs">/ {allocation.totalUnits}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
